import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Shield, UserPlus, X, Loader2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { createAdminInvitation, getAdminInvitations, revokeAdminInvitation } from '@/lib/api';

export const AdminInvitesCard = () => {
  const { t } = useTranslation();
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [fallbackLink, setFallbackLink] = useState(null);

  const load = () => getAdminInvitations().then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await createAdminInvitation({ email, personal_message: message || null, app_origin: window.location.origin });
      if (res.data.email_sent) {
        toast.success(t('adminInvite.sent'));
        setOpen(false);
      } else {
        setFallbackLink(res.data.invite_link);
      }
      setEmail(''); setMessage('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id) => {
    try { await revokeAdminInvitation(id); toast.success(t('adminInvite.revoked')); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Fehler'); }
  };

  const pending = invites.filter((i) => i.status === 'PENDING');

  return (
    <Card className="border-red-200/60 dark:border-red-900/40" data-testid="admin-invites-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-base md:text-lg"><Shield className="h-4 w-4 text-red-500" /> {t('adminInvite.pending')}</CardTitle>
          <CardDescription>{t('adminInvite.desc')}</CardDescription>
        </div>
        <Button size="sm" onClick={() => { setFallbackLink(null); setOpen(true); }} className="bg-red-600 hover:bg-red-700 gap-1 shrink-0" data-testid="invite-admin-btn">
          <UserPlus className="h-4 w-4" /> {t('adminInvite.button')}
        </Button>
      </CardHeader>
      <CardContent>
        {pending.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="admin-invites-empty">{t('adminInvite.none')}</p>
        ) : (
          <ul className="space-y-2" data-testid="admin-invites-list">
            {pending.map((inv) => (
              <li key={inv.invite_id} className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm" data-testid={`admin-invite-${inv.invite_id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{inv.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {inv.invite_id} · {t('adminInvite.invitedBy')} {inv.invited_by_name} · {t('adminInvite.expires')} {new Date(inv.expiry_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">{inv.status}</Badge>
                  <Button size="sm" variant="ghost" className="h-8 text-red-600 gap-1" onClick={() => revoke(inv.invite_id)} data-testid={`revoke-admin-invite-${inv.invite_id}`}>
                    <X className="h-3.5 w-3.5" /> {t('adminInvite.revoke')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-admin-dialog">
          <DialogHeader>
            <DialogTitle>{t('adminInvite.title')}</DialogTitle>
            <DialogDescription>{t('adminInvite.desc')}</DialogDescription>
          </DialogHeader>
          {fallbackLink ? (
            <div className="space-y-2" data-testid="invite-admin-fallback">
              <p className="text-sm text-amber-600">{t('adminInvite.sentNoMail')}</p>
              <div className="flex gap-2">
                <Input readOnly value={fallbackLink} data-testid="invite-admin-link" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard?.writeText(fallbackLink); toast.success('Kopiert'); }}><Copy className="h-4 w-4" /></Button>
              </div>
              <DialogFooter><Button onClick={() => setOpen(false)}>{t('common.close', 'Schließen')}</Button></DialogFooter>
            </div>
          ) : (
            <form onSubmit={send} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="inv-email">{t('adminInvite.email')}</Label>
                <Input id="inv-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-admin-email" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inv-msg">{t('adminInvite.message')}</Label>
                <Textarea id="inv-msg" maxLength={500} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="invite-admin-message" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
                <Button type="submit" disabled={sending} className="bg-red-600 hover:bg-red-700" data-testid="invite-admin-submit">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('adminInvite.send')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
